import { tileKey, type GameStore, type WorldState } from "@dawn/engine";
import type { StoragePort } from "./ports.ts";
import {
  clearWorldSavedGame,
  DEFAULT_WORLD_HOST_PRESENTATION_PREFS,
  isResumableWorldPhase,
  setWorldSavedGame,
  type WorldHostPresentationPrefs,
} from "./worldSaveStorage.ts";

export interface AttachWorldSavePersistenceOptions {
  getPresentation?: () => WorldHostPresentationPrefs;
  storage?: StoragePort | null;
}

/**
 * Auto-saves WorldState + host presentation prefs when turn, phase, or clan
 * position/move advances into a resumable phase. Clears storage on victory/defeat.
 */
export function attachWorldSavePersistence(
  store: GameStore,
  options: AttachWorldSavePersistenceOptions = {},
): () => void {
  const getPresentation =
    options.getPresentation ?? (() => ({ ...DEFAULT_WORLD_HOST_PRESENTATION_PREFS }));
  const storage = options.storage;
  let lastTurn = store.getState().turn;
  let lastPhase = store.getState().phase;
  let lastMoved = store.getState().clan.movedThisTurn;
  let lastClanTile = tileKey(
    store.getState().clan.currentTile.q,
    store.getState().clan.currentTile.r,
  );

  return store.subscribe((state: WorldState) => {
    const turnChanged = state.turn !== lastTurn;
    const phaseChanged = state.phase !== lastPhase;
    const movedChanged = state.clan.movedThisTurn !== lastMoved;
    const nextClanTile = tileKey(state.clan.currentTile.q, state.clan.currentTile.r);
    const tileChanged = nextClanTile !== lastClanTile;

    lastTurn = state.turn;
    lastPhase = state.phase;
    lastMoved = state.clan.movedThisTurn;
    lastClanTile = nextClanTile;

    if (!turnChanged && !phaseChanged && !movedChanged && !tileChanged) {
      return;
    }

    if (state.phase === "victory" || state.phase === "defeat") {
      clearWorldSavedGame(storage);
      return;
    }

    if (isResumableWorldPhase(state.phase)) {
      setWorldSavedGame(state, getPresentation(), store.getMapScenario(), storage);
    }
  });
}
